import { useTranslation } from "@privacy/translations";
import { useQuery } from "@tanstack/react-query";
import { systemHistoryQueryOptions, useGetSystemById } from "../../../api/systems";
import { DynamicGrid } from "../../../dynamicGrid";
import { DynamicHistory } from "../../../dynamicHistory";
import { logUnhandled } from "../../../api/logError";
import { InformationTypeIcon3 } from "../../../shared/icons";
import { ClassificationScoresHistoryView } from "../../../classification/ClassificationScoresHistoryView";
import { Drawer } from "../../../shared/Drawer";
import { SystemsDrawerHeader } from "./SystemsDrawerHeader";

export const SystemHistoryDrawer = () => {
  const systemId = DynamicGrid.useSelectedRowId();
  const { t } = useTranslation();
  const systemHistoryQuery = useQuery(systemHistoryQueryOptions(systemId));
  const systemByIdQuery = useGetSystemById(systemId);

  return (
    <DynamicGrid.Drawer>
      {(() => {
        if (!systemByIdQuery.data) return <Drawer.Loading />;

        return (
          <>
            <SystemsDrawerHeader system={systemByIdQuery.data} activeTab="history" statusIndicator={"hidden"} />

            {!systemHistoryQuery.data ? (
              <Drawer.Loading />
            ) : (
              <DynamicHistory.Entries
                historyEntries={systemHistoryQuery.data.history}
                renderCustomEntry={entry => {
                  switch (entry.customType) {
                    case "systemName":
                      return (
                        <DynamicHistory.BeforeAndAfterEntry
                          {...entry}
                          header={t("System.Name")}
                          before={<DynamicHistory.SnapshotValueText>{entry.oldValue}</DynamicHistory.SnapshotValueText>}
                          after={<DynamicHistory.SnapshotValueText>{entry.newValue}</DynamicHistory.SnapshotValueText>}
                        />
                      );
                    case "systemClassificationOverride":
                      return (
                        <DynamicHistory.BeforeAndAfterEntry
                          {...entry}
                          header={t("History.SystemClassificationOverride.Header")}
                          before={
                            entry.oldValue ? (
                              <ClassificationScoresHistoryView
                                scores={entry.oldValue.scores}
                                rationale={entry.oldValue.rationale}
                              />
                            ) : (
                              <DynamicHistory.EmptyValue />
                            )
                          }
                          after={
                            entry.newValue ? (
                              <ClassificationScoresHistoryView
                                scores={entry.newValue.scores}
                                rationale={entry.newValue.rationale}
                              />
                            ) : (
                              <DynamicHistory.EmptyValue />
                            )
                          }
                        />
                      );
                    case "systemConnectedInformationType": {
                      const connectionType = (entry.newValue ?? entry.oldValue)?.connectionType;
                      if (!connectionType)
                        throw new Error("systemConnectedInformationType history entry is missing connectionType");
                      return (
                        <DynamicHistory.BeforeAndAfterEntry
                          {...entry}
                          header={t(`History.Connection.${connectionType}`)}
                          before={
                            entry.oldValue && entry.oldValue.connections.length > 0 ? (
                              <DynamicHistory.MultipleValues>
                                {entry.oldValue.connections.map(connection => (
                                  <DynamicHistory.SnapshotValueText
                                    Icon={InformationTypeIcon3}
                                    key={connection.informationTypeId}
                                  >
                                    {connection.informationTypeName}
                                  </DynamicHistory.SnapshotValueText>
                                ))}
                              </DynamicHistory.MultipleValues>
                            ) : (
                              <DynamicHistory.EmptyValue />
                            )
                          }
                          after={
                            entry.newValue && entry.newValue.connections.length > 0 ? (
                              <DynamicHistory.MultipleValues>
                                {entry.newValue.connections.map(connection => (
                                  <DynamicHistory.SnapshotValueText
                                    Icon={InformationTypeIcon3}
                                    key={connection.informationTypeId}
                                  >
                                    {connection.informationTypeName}
                                  </DynamicHistory.SnapshotValueText>
                                ))}
                              </DynamicHistory.MultipleValues>
                            ) : (
                              <DynamicHistory.EmptyValue />
                            )
                          }
                        />
                      );
                    }
                    case "archived":
                      return entry.newValue ? (
                        <DynamicHistory.ArchivedEntry createdAt={entry.createdAt} createdBy={entry.createdBy} />
                      ) : (
                        <DynamicHistory.UnarchivedEntry createdAt={entry.createdAt} createdBy={entry.createdBy} />
                      );
                    default:
                      return logUnhandled(entry, "custom entry");
                  }
                }}
              />
            )}
          </>
        );
      })()}
    </DynamicGrid.Drawer>
  );
};
